import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

interface Reference {
  id: string;
  name: string;
  logo_url: string;
  website_url: string | null;
}

// Static imports for initial references
import pelikanLogo from "@/assets/references/pelikan-sk.png";
import heinekenLogo from "@/assets/references/heineken-slovensko.jpg";
import dhlLogo from "@/assets/references/dhl.png";
import hydrotourLogo from "@/assets/references/hydrotour.png";

const staticReferences = [
  { id: "1", name: "Pelikan.sk", logo_url: pelikanLogo, website_url: "https://pelikan.sk" },
  { id: "2", name: "Heineken Slovensko", logo_url: heinekenLogo, website_url: "https://heinekenslovensko.sk" },
  { id: "3", name: "DHL", logo_url: dhlLogo, website_url: "https://dhl.sk" },
  { id: "4", name: "Hydrotour", logo_url: hydrotourLogo, website_url: "https://hydrotour.sk" },
];

const ReferencesSection = () => {
  const [references, setReferences] = useState<Reference[]>(staticReferences);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const { data, error } = await supabase
          .from("client_references")
          .select("id, name, logo_url, website_url")
          .eq("is_active", true)
          .order("sort_order", { ascending: true });

        if (error) throw error;
        
        // Use database references if available, otherwise use static
        if (data && data.length > 0) {
          setReferences(data);
        }
      } catch (error) {
        console.error("Error fetching references:", error);
        // Keep using static references on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferences();
  }, []);

  return (
    <section className="py-20 bg-background" aria-labelledby="references-heading">
      <div className="container mx-auto px-4">
        <header className="text-center mb-14">
          <span className="inline-block text-secondary font-semibold text-sm uppercase tracking-wider mb-4">
            {t('references.badge', 'Referencie')}
          </span>
          <h2 id="references-heading" className="text-3xl md:text-4xl font-bold mb-4 text-foreground">{t('references.title', 'Dôverujú nám')}</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t('references.subtitle', 'Spolupracujeme s poprednými slovenskými aj medzinárodnými spoločnosťami')}
          </p>
        </header>

        <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 lg:gap-14">
          {references.map((ref) => (
            <a
              key={ref.id}
              href={ref.website_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center p-6 bg-card rounded-2xl border border-border hover:border-secondary/50 hover:shadow-lg transition-all duration-300"
              title={ref.name}
            >
              <img
                src={ref.logo_url}
                alt={`Logo spoločnosti ${ref.name} - klient Prelozim.sk pre úradné preklady`}
                className="h-16 md:h-20 max-w-[200px] object-contain"
                width={200}
                height={80}
                loading="lazy"
              />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ReferencesSection;
